import { I18n, type TranslateOptions } from 'i18n-js';

import common from './src/locales/common';
import customer from './src/locales/customer';
import mobile from './src/locales/mobile';
import mobilePro from './src/locales/mobile-pro';
import mobileUI from './src/locales/mobile-ui';
import pro from './src/locales/pro';
import web from './src/locales/web';
import webCustomerArea from './src/locales/web-customer-area';
import webItc from './src/locales/web-itc';
import type { Locale, Translations } from './src/types';

type ErrorNotifier = (error: Error) => void;

const i18n = new I18n();

const isTestOrDev = process.env.NODE_ENV === 'test' || process.env.NODE_ENV === 'development';

const exists = (key: string, values?: Values): boolean =>
  i18n.t(key, { ...values, defaultValue: 'missing-key' }) !== 'missing-key';

const genderBySalutation: Record<Salutation, 'male' | 'female'> = {
  'M.': 'male',
  Mme: 'female',
};

const formatKey = (key: string, values?: Values): string => {
  if (!values) return key;
  if (values.salutation) {
    const salutationKey = `${key}.${genderBySalutation[values.salutation]}`;
    if (exists(salutationKey, values)) return salutationKey;
  }
  return key;
};

// Phrase forbids saving an empty translation, so translators use this placeholder
// to explicitly mark a translation as intentionally empty. We turn it back into ''.
const EMPTY_TRANSLATION_PLACEHOLDER = '[empty]';

type Salutation = 'M.' | 'Mme';

export type Values = TranslateOptions & { salutation?: Salutation };

type Translate = (prefix?: string) => (key: string, values?: Values) => string;

export const translate: Translate =
  (prefix = '') =>
  (key, values) => {
    const fullKey = key.startsWith('.') ? `${prefix}${key}` : key;
    const formattedKey = formatKey(fullKey, values);

    const translation = i18n.t(formattedKey, values);
    return translation === EMPTY_TRANSLATION_PLACEHOLDER ? '' : translation;
  };

type GetSupportedLocaleArgs = {
  locale: string; // string as it could come from device API or anywhere else
  supportedLocales: Locale[];
  defaultLocale: Locale;
};

export const getSupportedLocale = ({ locale, supportedLocales, defaultLocale }: GetSupportedLocaleArgs): Locale => {
  // matches exactly two lowercase letters, optionally followed by a hyphen and two uppercase letters
  const matches = locale.match(/^[a-z]{2}(-[A-Z]{2})?$/);
  const match = matches?.length ? (matches[0] as Locale) : null;

  // For a ll-CC locale, first check if ll-CC is supported
  if (match && supportedLocales.includes(match as Locale)) {
    return match;
  }

  return defaultLocale;
};

type SetI18nLocaleConfigParams = {
  locale: string;
  supportedLocales: Locale[];
  defaultLocale: Locale;
};

// Used on app init, and when switching country for example
export const setI18nLocaleConfig = ({ locale, supportedLocales, defaultLocale }: SetI18nLocaleConfigParams) => {
  i18n.locale = getSupportedLocale({ locale, supportedLocales, defaultLocale });
  i18n.defaultLocale = defaultLocale;
};

type SetI18nConfigArgs = {
  translations: {
    [locale: string]: object;
  };
  locale: string;
  supportedLocales: Locale[];
  defaultLocale: Locale;
  notifier?: ErrorNotifier;
};

export const setI18nConfig = ({
  translations,
  locale,
  supportedLocales,
  defaultLocale,
  notifier,
}: SetI18nConfigArgs) => {
  i18n.store(translations);
  i18n.enableFallback = true;
  setI18nLocaleConfig({ locale, supportedLocales, defaultLocale });
  i18n.missingTranslationPrefix = '';
  i18n.missingBehavior = isTestOrDev ? 'error' : 'message';
  i18n.missingTranslation.register('message', (_, scope) => {
    const message = `missing ${scope} translation for ${i18n.locale}`;
    if (notifier) notifier(new Error(message));
    return message;
  });
  // message is the translation text as defined in the json file
  i18n.missingPlaceholder = (_, placeholder, text) => {
    const message = `missing ${placeholder} value for ${i18n.locale} inside translation "${text}"`;
    if (isTestOrDev) throw new Error(message);
    if (notifier) notifier(new Error(message));
    return message;
  };
};

// For now, this function is only used for testing to change the current locale between tests.
export const setI18nLocale = (locale: Locale) => {
  setI18nLocaleConfig({ locale, supportedLocales: [locale], defaultLocale: locale });
};

export const getI18nLocale = (): Locale => i18n.locale as Locale;

export type { Locale, Translations };
export { common, customer, mobile, mobilePro, mobileUI, pro, web, webCustomerArea, webItc };

export default i18n;
